import { Language, promosLangForLanguage } from '../i18n/languages';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  auth_provider: string;
  email_verified: boolean;
  has_avatar?: boolean;
  created_at?: string;
}

export interface TokenResponse {
  status: string;
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: AuthUser;
}

const STORAGE_ACCESS = 'surf_access_token';
const STORAGE_REFRESH = 'surf_refresh_token';

export function getStoredTokens(): { access: string | null; refresh: string | null } {
  return {
    access: localStorage.getItem(STORAGE_ACCESS),
    refresh: localStorage.getItem(STORAGE_REFRESH),
  };
}

export function storeTokens(access: string, refresh: string): void {
  localStorage.setItem(STORAGE_ACCESS, access);
  localStorage.setItem(STORAGE_REFRESH, refresh);
}

export function clearTokens(): void {
  localStorage.removeItem(STORAGE_ACCESS);
  localStorage.removeItem(STORAGE_REFRESH);
}

export function getAccessToken(): string | null {
  return localStorage.getItem(STORAGE_ACCESS);
}

async function parseJson(res: Response): Promise<Record<string, unknown>> {
  try {
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export async function register(
  email: string,
  password: string,
  displayName: string,
  language: Language = 'es'
): Promise<{ ok: boolean; message?: string; emailSent?: boolean; error?: string }> {
  const res = await fetch(`${API_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      password,
      display_name: displayName,
      language: promosLangForLanguage(language),
    }),
  });
  const json = await parseJson(res);
  if (!res.ok) return { ok: false, error: (json.detail as string) || 'Error al registrarse' };
  return {
    ok: true,
    message: (json.message as string) || 'Revisa tu correo',
    emailSent: json.email_sent !== false,
  };
}

export async function resendVerificationEmail(
  email: string,
  language: Language = 'es'
): Promise<{ ok: boolean; emailSent?: boolean; error?: string }> {
  const res = await fetch(`${API_URL}/auth/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, language: promosLangForLanguage(language) }),
  });
  const json = await parseJson(res);
  if (!res.ok) return { ok: false, error: (json.detail as string) || 'Error al reenviar' };
  return { ok: true, emailSent: json.email_sent !== false };
}

export async function requestPasswordReset(
  email: string,
  language: Language = 'es'
): Promise<{ ok: boolean; emailSent?: boolean; error?: string }> {
  const res = await fetch(`${API_URL}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, language: promosLangForLanguage(language) }),
  });
  const json = await parseJson(res);
  if (!res.ok) return { ok: false, error: (json.detail as string) || 'Error al enviar el enlace' };
  return { ok: true, emailSent: json.email_sent !== false };
}

export async function resetPassword(
  token: string,
  password: string
): Promise<{ ok: boolean; error?: string }> {
  const res = await fetch(`${API_URL}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });
  const json = await parseJson(res);
  if (!res.ok) return { ok: false, error: (json.detail as string) || 'No se pudo restablecer la contraseña' };
  return { ok: true };
}

export async function login(email: string, password: string): Promise<TokenResponse | { error: string }> {
  const res = await fetch(`${API_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const json = await parseJson(res);
  if (!res.ok) return { error: (json.detail as string) || 'Error al iniciar sesión' };
  const data = json as unknown as TokenResponse;
  storeTokens(data.access_token, data.refresh_token);
  return data;
}

export async function refreshSession(): Promise<AuthUser | null> {
  const { refresh } = getStoredTokens();
  if (!refresh) return null;
  const res = await fetch(`${API_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refresh }),
  });
  const json = await parseJson(res);
  if (!res.ok) {
    clearTokens();
    return null;
  }
  const data = json as unknown as TokenResponse;
  storeTokens(data.access_token, data.refresh_token);
  return data.user;
}

export async function fetchMe(): Promise<AuthUser | null> {
  const token = getAccessToken();
  if (!token) return null;
  const res = await fetch(`${API_URL}/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) {
    return refreshSession();
  }
  const json = await parseJson(res);
  if (!res.ok) return null;
  return (json.user as AuthUser) || null;
}

export async function logoutApi(): Promise<void> {
  const { refresh } = getStoredTokens();
  if (refresh) {
    await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refresh }),
    });
  }
  clearTokens();
}

export async function exchangeOAuthToken(exchangeToken: string): Promise<TokenResponse | { error: string }> {
  const res = await fetch(`${API_URL}/auth/oauth/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ exchange_token: exchangeToken }),
  });
  const json = await parseJson(res);
  if (!res.ok) return { error: (json.detail as string) || 'OAuth fallido' };
  const data = json as unknown as TokenResponse;
  storeTokens(data.access_token, data.refresh_token);
  return data;
}

export function googleLoginUrl(): string {
  return `${API_URL}/auth/google`;
}
